import { Button } from '@affine/component';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { type FC, useEffect, useState } from 'react';

import * as settingStyles from '../automation-settings-panel.css';
import { DataRow } from './history-row-variants/row-primitives';
import {
  type AutomationPlanUsage,
  type PlanUsageRow,
  type PlanUsageRowId,
  planUsageRows,
} from './plan-usage';

const LABEL_KEYS = {
  'concurrent-runs': 'com.streamlain.automation.plan-usage.concurrent-runs',
  'webhook-concurrent-runs':
    'com.streamlain.automation.plan-usage.webhook-concurrent-runs',
  'webhook-automations':
    'com.streamlain.automation.plan-usage.webhook-automations',
  'webhook-runs-per-minute':
    'com.streamlain.automation.plan-usage.webhook-runs-per-minute',
  'webhook-data-per-minute':
    'com.streamlain.automation.plan-usage.webhook-data-per-minute',
} as const satisfies Record<PlanUsageRowId, string>;

const TONES = { ok: undefined, 'at-limit': 'warning', over: 'error' } as const;

/**
 * How much of each plan limit the automation uses. Reloaded when its
 * trigger, webhook or switch changes.
 */
export const PlanUsageSection: FC<{
  workspaceId: string;
  blockId: string;
  /** Changes whenever the use can have changed. */
  refreshKey: string;
}> = ({ workspaceId, blockId, refreshKey }) => {
  const t = useI18n();
  const workspaceDialogService = useService(WorkspaceDialogService);
  const [usage, setUsage] = useState<AutomationPlanUsage | null>(null);

  useEffect(() => {
    let current = true;
    workspaceApiFetch(
      `/api/workspace/${workspaceId}/workflow/configs/${blockId}/plan-usage`,
      { credentials: 'include' },
      {
        workspaceId,
        category: 'workspace-api',
        key: `automation-plan-usage:${blockId}`,
        mode: 'background',
        priority: 'background',
      }
    )
      .then(res =>
        res.ok ? (res.json() as Promise<AutomationPlanUsage>) : null
      )
      .then(data => {
        if (current) setUsage(data);
      })
      .catch(error => {
        console.error(error);
        if (current) setUsage(null);
      });
    return () => {
      current = false;
    };
  }, [blockId, refreshKey, workspaceId]);

  if (!usage) return null;
  const rows = planUsageRows(usage);
  const megabytes = new Intl.NumberFormat(undefined, {
    style: 'unit',
    unit: 'megabyte',
    maximumFractionDigits: 1,
  });
  const amount = (row: PlanUsageRow, value: number | null) =>
    value === null
      ? '–'
      : row.id === 'webhook-data-per-minute'
        ? megabytes.format(value / (1024 * 1024))
        : String(value);
  const valueText = (row: PlanUsageRow) =>
    row.value.max === null
      ? t['com.streamlain.automation.plan-usage.unlimited']({
          current: amount(row, row.value.current),
        })
      : t['com.streamlain.automation.plan-usage.value']({
          current: amount(row, row.value.current),
          max: amount(row, row.value.max),
        });
  const overBindings = rows.some(
    row => row.id === 'webhook-automations' && row.state === 'over'
  );
  const atLimit = rows.some(row => row.state !== 'ok');

  return (
    <div data-testid="automation-plan-usage">
      <span className={settingStyles.settingLabel}>
        {t['com.streamlain.automation.plan-usage.title']()}
      </span>
      {rows.map(row => (
        <DataRow
          key={row.id}
          label={t[LABEL_KEYS[row.id]]()}
          value={valueText(row)}
          tone={TONES[row.state]}
        />
      ))}
      {atLimit && (
        <>
          <span className={settingStyles.settingHint}>
            {overBindings
              ? t['com.streamlain.automation.plan-usage.over-automations']()
              : t['com.streamlain.automation.plan-usage.at-limit']()}
          </span>
          <Button
            variant="secondary"
            onClick={() =>
              workspaceDialogService.open('setting', {
                activeTab: 'workspace:plans',
              })
            }
          >
            {t['com.streamlain.automation.plan-usage.plans']()}
          </Button>
        </>
      )}
    </div>
  );
};
